// Step 3 — read-only summary. Totals weight against the truck's payload
// limit and flags over-capacity in red before the user commits to Simulate.

import React, { useMemo } from 'react';
import { Row, Col, Card, Table, Badge, Alert } from 'react-bootstrap';

import { typeBadgeColor } from '../../../features/warehouse/warehouseUtils.js';

export default function StepReview({ truck, items }) {
  const totals = useMemo(() => {
    const byType = {};
    let lengthM = 0;
    let weightKg = 0;
    for (const it of items) {
      byType[it.type] = (byType[it.type] || 0) + 1;
      lengthM += Number(it.lengthMeters || 0);
      weightKg += Number(it.weightKg || 0);
    }
    return { count: items.length, byType, lengthM, weightKg };
  }, [items]);

  const overWeight = truck.maxPayloadKg && totals.weightKg > truck.maxPayloadKg;

  return (
    <>
      <Row className="g-3 mb-3">
        <Col md={3}>
          <Summary label="Items" value={totals.count} />
        </Col>
        <Col md={3}>
          <Summary
            label="Total weight"
            value={`${totals.weightKg.toFixed(0)} kg`}
            sub={truck.maxPayloadKg ? `/ ${truck.maxPayloadKg} kg max` : null}
            danger={overWeight}
          />
        </Col>
        <Col md={3}>
          <Summary
            label="Total cable"
            value={`${(totals.lengthM / 1000).toFixed(2)} km`}
          />
        </Col>
        <Col md={3}>
          <Summary
            label="Mix"
            value={Object.entries(totals.byType)
              .map(([t, n]) => `${n} ${t}`)
              .join(', ') || '—'}
          />
        </Col>
      </Row>

      {overWeight && (
        <Alert variant="danger">
          The load weighs {totals.weightKg.toFixed(0)} kg —{' '}
          <strong>{(totals.weightKg - truck.maxPayloadKg).toFixed(0)} kg over</strong>
          the truck's payload limit. Remove items, split into two runs, or pick a
          bigger truck.
        </Alert>
      )}

      <Card>
        <Card.Header>
          <strong>Load list</strong>{' '}
          <span className="text-muted small">
            · {truck.label} · {truck.lengthM} × {truck.widthM} × {truck.heightM} m
          </span>
        </Card.Header>
        <Card.Body className="p-0" style={{ maxHeight: 420, overflowY: 'auto' }}>
          <Table size="sm" hover className="mb-0">
            <thead className="sticky-top bg-white">
              <tr>
                <th>#</th>
                <th>Source</th>
                <th>Serial</th>
                <th>Type</th>
                <th>Cable</th>
                <th className="text-end">Length (m)</th>
                <th className="text-end">Weight (kg)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it._id || i}>
                  <td>{i + 1}</td>
                  <td>
                    <Badge bg={it.source === 'manual' ? 'warning' : 'info'} text="dark">
                      {it.source}
                    </Badge>
                  </td>
                  <td><code>{it.serial}</code></td>
                  <td><Badge bg={typeBadgeColor(it.type)}>{it.type}</Badge></td>
                  <td className="small">{it.cableName}</td>
                  <td className="text-end">{Number(it.lengthMeters || 0).toFixed(0)}</td>
                  <td className="text-end">{Number(it.weightKg || 0).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
}

function Summary({ label, value, sub, danger }) {
  return (
    <Card className={`h-100 ${danger ? 'border-danger' : ''}`}>
      <Card.Body>
        <div className="text-muted small text-uppercase">{label}</div>
        <div className={`h4 mb-0 mt-1 ${danger ? 'text-danger' : ''}`}>{value}</div>
        {sub && <div className="small text-muted">{sub}</div>}
      </Card.Body>
    </Card>
  );
}
